/**
 * Formato do ARQUIVO (PDF, DOCX, …), derivado do mime type.
 *
 * Não confundir com `Documento.tipo`, que é a natureza do documento no negócio
 * (CONTRATO, DRE, NOTA_FISCAL…). São eixos independentes: um contrato pode chegar como
 * PDF ou como DOCX. Ícone e cor são do formato; o rótulo de negócio vem do `tipo`.
 */
export type FormatoArquivo = 'PDF' | 'DOCX' | 'XLSX' | 'PNG' | 'JPG' | 'OUTRO'

export function formatoDoMime(mime: string | undefined): FormatoArquivo {
  if (!mime) return 'OUTRO'
  const m = mime.toLowerCase()
  if (m.includes('pdf')) return 'PDF'
  if (m.includes('wordprocessingml') || m.includes('msword')) return 'DOCX'
  if (m.includes('spreadsheetml') || m.includes('excel')) return 'XLSX'
  if (m.includes('png')) return 'PNG'
  if (m.includes('jpeg') || m.includes('jpg')) return 'JPG'
  return 'OUTRO'
}

const ICONES: Record<FormatoArquivo, string> = {
  PDF: '📄',
  DOCX: '📝',
  XLSX: '📊',
  PNG: '🖼️',
  JPG: '🖼️',
  OUTRO: '📎',
}

const CORES: Record<FormatoArquivo, string> = {
  PDF: '#D32F2F',
  DOCX: '#3B82F6',
  XLSX: '#388E3C',
  PNG: '#7B1FA2',
  JPG: '#7B1FA2',
  OUTRO: '#757575',
}

export const iconeFormato = (f: FormatoArquivo): string => ICONES[f]
export const corFormato = (f: FormatoArquivo): string => CORES[f]

export function formatarTamanho(bytes: number | undefined): string {
  if (!bytes) return '0 B'
  const k = 1024
  const unidades = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + unidades[i]
}

/**
 * O backend grava o risco como texto livre; já apareceu com e sem acento. Normaliza para
 * o vocabulário da interface em vez de deixar um `MEDIO` cair no ramo de erro por não
 * bater com `MÉDIO`.
 */
export type NivelRisco = 'BAIXO' | 'MEDIO' | 'ALTO'

export function normalizarRisco(bruto: string | undefined): NivelRisco | null {
  if (!bruto) return null
  const s = bruto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // tira os acentos combinantes
    .toUpperCase()
    .trim()
  if (s.startsWith('BAIX')) return 'BAIXO'
  if (s.startsWith('MED')) return 'MEDIO'
  if (s.startsWith('ALT')) return 'ALTO'
  return null
}

export const corRisco = (n: NivelRisco): 'success' | 'warning' | 'error' =>
  n === 'BAIXO' ? 'success' : n === 'MEDIO' ? 'warning' : 'error'

export const rotuloRisco = (n: NivelRisco): string =>
  n === 'MEDIO' ? 'MÉDIO' : n
